import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  HostListener,
  input,
  model,
  output,
  signal,
  ViewChild
} from '@angular/core';

/**
 * A floating box component that positions itself relative to a trigger element.
 *
 * @example
 * <umss-popover [(open)]="isOpen">
 *   <div class="popover-content">This is the popover content.</div>
 * </umss-popover>
 */
@Component({
  selector: 'umss-popover',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './popover.component.html',
  styleUrl: './popover.component.scss'
})
export class UmssPopoverComponent {
  /** Whether the popover is currently visible. Supports two-way binding. */
  open = model<boolean>(false);

  /** Width of the popover box. Defaults to '280px' to match distance panel. */
  width = input<string>('280px');

  /** Emits when the popover is closed (e.g., clicked outside or Escape pressed). */
  closePopover = output<void>();

  @ViewChild('popoverPanel') popoverPanel!: ElementRef<HTMLElement>;

  /** Internal calculated positions */
  protected verticalPosition = signal<'top' | 'bottom'>('bottom');
  protected horizontalPosition = signal<'left' | 'right'>('left');

  constructor(private elementRef: ElementRef) {
    effect(() => {
      if (this.open()) {
        this.calculatePosition();
      }
    });
  }

  /**
   * Close on outside click.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;

    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.close();
    }
  }

  /**
   * Recalculate position on window resize.
   */
  @HostListener('window:resize')
  onResize(): void {
    if (this.open()) {
      this.calculatePosition();
    }
  }

  /**
   * Close on Escape key.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  /**
   * Public method to toggle the popover visibility.
   */
  toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.show();
    }
  }

  /**
   * Displays the popover and calculates its position.
   */
  show(): void {
    this.open.set(true);
  }

  /**
   * Hides the popover.
   */
  close(): void {
    this.open.set(false);
    this.closePopover.emit();
  }

  /**
   * Calculates the best position for the popover panel based on available space.
   * Priority is bottom and moved to the right (meaning the left edge of popover
   * aligns with the left edge of the trigger).
   */
  private calculatePosition(): void {
    const host = this.elementRef.nativeElement;
    // Attempt to find a trigger: either a preceding sibling or the parent
    let trigger = (host.previousElementSibling ||
      host.parentElement) as HTMLElement;

    if (!trigger) return;

    // If the trigger has display: contents, it doesn't have its own box.
    // We need to find its first child that actually has a layout box.
    if (window.getComputedStyle(trigger).display === 'contents') {
      const firstChild = Array.from(trigger.children).find(
        (child) => window.getComputedStyle(child).display !== 'contents'
      ) as HTMLElement;

      if (firstChild) {
        trigger = firstChild;
      }
    }

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Popover estimated dimensions
    const popoverHeight = this.popoverPanel?.nativeElement.offsetHeight || 200;
    const popoverWidth = parseInt(this.width()) || 280;

    // Vertical position calculation
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
      this.verticalPosition.set('top');
    } else {
      this.verticalPosition.set('bottom');
    }

    // Horizontal position calculation
    // Priority: 'left' (align left edge of popover with left edge of trigger, grow to the right)
    // If it doesn't fit on the right, check if it fits on the left (align right edge, grow left)

    // Space available from trigger's left edge to the right edge of viewport
    const spaceRight = viewportWidth - rect.left;
    // Space available from left edge of viewport to the trigger's right edge
    const spaceLeft = rect.right;

    if (spaceRight < popoverWidth && spaceLeft >= popoverWidth) {
      // Not enough room on the right, but enough room on the left
      this.horizontalPosition.set('right');
    } else {
      // Default to left alignment (priority)
      // Even if it doesn't fit, 'left' is the requested default priority
      this.horizontalPosition.set('left');
    }
  }
}
